import React from 'react';

import {
    Accordion, AccordionDetails, AccordionSummary, Box, Link, Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import Code from '../codeHighlighting';

/**
 * "How to connect to MQTT" — a default-folded accordion shown above the
 * dataset table on the Admin / My MQTT tab. Visible to BOTH admin- and
 * user-mode viewers because the connection mechanics are identical
 * regardless of who set the broker up.
 *
 * Documented connection method (single canonical path):
 *   - WSS on port 443 to the dataset's broker hostname.
 *   - The JWT is the MQTT CONNECT **username**; the password is ignored.
 *   - The clientId must be derived from the token:
 *     sha256(token)[:12], optionally followed by `-<suffix>`. The binding is
 *     enforced on the ACL check, NOT at CONNECT: a wrong clientId still
 *     connects, then its first SUBSCRIBE/PUBLISH is denied (v5 reason 135;
 *     silent on 3.1.1). The wcibapi log shows `clientid-unbound`.
 *
 * The broker runs mosquitto-go-auth's `jwt` backend in remote mode, which
 * takes the token from the username field and forwards it to wcibapi as
 * `Authorization: Bearer` on every CONNECT and every ACL check. A client's
 * own WSS-upgrade Authorization header is NOT usable: mosquitto never
 * exposes the HTTP upgrade headers to the auth plugin.
 *
 * Self-signed certs: the per-row MqttInfo panel above the guide
 * surfaces a "Download .pem" button when applicable. This guide just
 * refers to that affordance instead of duplicating download logic.
 *
 * Scope of this component is documentation only — no fetching, no
 * Redux. It exists as its own file so the long copy stays out of
 * Admin.js and so we can write a small focused test.
 */

// Single canonical example: WSS, JWT as the MQTT username, clientId derived
// from the token, optional ca_certs for self-signed brokers (path the user
// has already saved from the Download .pem button on the MqttInfo panel).
const PAHO_EXAMPLE = `import hashlib
import paho.mqtt.client as mqtt

BROKER_HOST = "mqtt-ds<DATASET_ID>.<env>.wara-ops.org"   # see panel above
BROKER_PORT = 443                                        # WSS / TLS
JWT_TOKEN   = "<paste from My JWTs>"
DATASET_ID  = <DATASET_ID>
TOPIC       = f"upload/v1/temperature/celsius/float"

# Self-signed brokers: download the .pem from the panel above and
# point ca_certs at it. Let's Encrypt brokers: omit ca_certs (uses
# system trust store).
CA_CERTS    = "/path/to/mqtt-ds<DATASET_ID>.pem"          # or None

def on_connect(client, userdata, flags, rc, props=None):
    print(f"connected rc={rc}")
    client.subscribe(TOPIC, qos=1)

def on_message(client, userdata, msg):
    print(f"{msg.topic} {msg.payload.decode()}")

# The clientId must be derived from the token (the connection succeeds with
# any other clientId, but the broker denies its first subscribe/publish, so
# nobody can hijack a session by guessing one).
CLIENT_ID = hashlib.sha256(JWT_TOKEN.encode()).hexdigest()[:12] + f"-my-client-{DATASET_ID}"

client = mqtt.Client(transport="websockets",
                     protocol=mqtt.MQTTv5,
                     client_id=CLIENT_ID)
client.ws_set_options(path="/mqtt")

# The JWT is the MQTT USERNAME; the password is never examined but must be
# non-empty.
client.username_pw_set(JWT_TOKEN, "unused")

client.tls_set(ca_certs=CA_CERTS)                         # None for LE brokers
client.on_connect = on_connect
client.on_message = on_message
client.connect(BROKER_HOST, BROKER_PORT, keepalive=60)
client.loop_forever()`;

// "My JWTs" lives behind the JupyterLink tab's "Generate Token" entry
// point in the current router. The deep link via /jwt is the stable
// entry — App.js redirects /jwt to /tabselect/JupyterLink with the
// gentokendialog=true flag set.
const MY_JWTS_HREF = '/tabselect/JupyterLink?gentokendialog=true';

export default function MqttConnectGuide() {
    return (
        <Accordion
            disableGutters
            defaultExpanded={false}
            sx={{ mb: 2 }}
            >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="mqtt-connect-guide-content"
                id="mqtt-connect-guide-header"
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HelpOutlineIcon fontSize="small" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        How to connect to MQTT
                    </Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails id="mqtt-connect-guide-content">
                <Typography variant="body2" paragraph>
                    WCIB MQTT brokers are reached over WebSockets Secure
                    (WSS). Authentication is a personal JWT sent as the MQTT
                    <code>username</code> — the token&apos;s scope decides
                    which topics you can subscribe to or publish on.
                </Typography>

                <Typography variant="subtitle2" sx={{ mt: 2 }}>1. Generate a token</Typography>
                <Typography variant="body2" paragraph>
                    Open <Link href={MY_JWTS_HREF}>My JWTs</Link> (under your
                    user menu), select the dataset you want, enable{' '}
                    <b>MQTT subscribe</b> (and <b>MQTT publish</b> if
                    you&apos;ll be sending data). Generate the token and
                    copy it immediately — it&apos;s not shown again.
                </Typography>

                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                    2. Connect over WSS with the token as the username
                </Typography>
                <Typography variant="body2" paragraph>
                    WCIB MQTT brokers accept connections over WebSockets
                    Secure (WSS) on the broker&apos;s hostname (port 443).
                    Put the JWT in the MQTT <b>username</b> field; the
                    password is never examined, so send any non-empty
                    placeholder. Your <b>clientId</b> must also be derived
                    from the token — that is what stops anyone else taking
                    over your session. The broker checks it on your first
                    subscribe or publish rather than at connect, so a wrong
                    clientId connects normally and then silently receives
                    nothing. Derive it with:
                </Typography>
                <Code language="bash" code={'printf %s "$TOKEN" | sha256sum | cut -c1-12'} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} paragraph>
                    Use that value as your clientId, optionally followed by
                    <code>-&lt;suffix&gt;</code>. Sending the token in the
                    MQTT password field, or in an HTTP Authorization header
                    on the WSS upgrade, is not accepted.
                </Typography>

                <Typography variant="subtitle2" sx={{ mt: 2 }}>3. Self-signed certificates</Typography>
                <Typography variant="body2" paragraph>
                    Some staging brokers use self-signed TLS certificates.
                    The dataset&apos;s MQTT panel above shows a{' '}
                    <b>Download .pem</b> button when this is the case.
                    Save the PEM and pass it to your client&apos;s
                    trust-store option (e.g. paho&apos;s{' '}
                    <code>tls_set(ca_certs=...)</code>). For brokers using
                    Let&apos;s Encrypt no extra step is needed — your
                    system trust store already trusts them.
                </Typography>

                <Typography variant="subtitle2" sx={{ mt: 2 }}>4. Topic format</Typography>
                <Typography variant="body2" paragraph>
                    Live data flows under{' '}
                    <code>upload/v1/&lt;column&gt;/&lt;unit&gt;/&lt;type&gt;</code>.
                    The full conventions (single-column, multi-column,
                    batch payloads) are documented inline on the{' '}
                    <Link href={MY_JWTS_HREF}>My JWTs</Link> page next to
                    the topic field — that&apos;s the source of truth.
                </Typography>

                <Typography variant="subtitle2" sx={{ mt: 2 }}>5. Example: Python (paho-mqtt)</Typography>
                <Code language="python" code={PAHO_EXAMPLE} />

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Token ACLs and topic permissions are described inline
                    on the <Link href={MY_JWTS_HREF}>My JWTs</Link> page.
                    Use QoS 1 while debugging — QoS 0 publishes are
                    silently dropped on auth/ACL failures with no
                    client-side feedback.
                </Typography>
            </AccordionDetails>
        </Accordion>
    );
}
